/**
 * "Describe your use case" modal — formerly the change-process catalog +
 * Claude-generation modal. Per the May 2026 functional/content review,
 * the in-modal catalog list was a duplicate of Story scene 2, and the
 * Claude API-key field exposed implementation details that don't belong
 * in a marketing surface.
 *
 * Today: a focused lead-capture form. The visitor describes the
 * operation they care about; we capture it + their work email and queue
 * a tailored simulation back. `lib/pilot/modals.js`'s
 * `openChangeProcessModal` flow is the wire-up surface; it submits via
 * `submitLead` and confirms in-modal.
 */

import React from 'react';
import {Button} from '../../oatmeal/elements/button';
import {Modal} from './_helpers';

export default function PilotChangeProcessModal() {
  return (
    <Modal id="modal-backdrop">
      <div className="modal-head flex items-center justify-between border-b border-taupe-200 px-6 py-4 dark:border-taupe-800">
        <h3 className="font-display text-2xl/8 text-taupe-950 dark:text-white">
          Describe your use case
        </h3>
        <button
          className="modal-close inline-flex size-9 items-center justify-center rounded-full text-taupe-700 hover:bg-taupe-100 dark:text-taupe-300 dark:hover:bg-taupe-800"
          id="modal-close"
          aria-label="Close"
        >
          ×
        </button>
      </div>
      <div className="modal-body flex flex-col gap-4 overflow-y-auto px-6 py-5" id="modal-body">
        <p className="text-sm/6 text-taupe-700 dark:text-taupe-400">
          Tell us about the operation you want to model. We'll run a
          tailored simulation against your numbers and send it back the same
          day.
        </p>
        <label className="modal-label flex flex-col gap-2 text-sm/6 font-medium text-taupe-950 dark:text-white">
          Your use case
          <textarea
            id="use-case-input"
            rows={4}
            placeholder="e.g. Onboarding a new vendor at a Fortune 500 retailer — from intake packet through first PO."
            className="rounded-lg border border-taupe-300 bg-white px-3 py-2 text-sm font-normal text-taupe-950 placeholder:text-taupe-500 focus:border-taupe-950 focus:outline-none focus:ring-2 focus:ring-taupe-300 dark:border-taupe-700 dark:bg-taupe-900 dark:text-white"
          />
        </label>
        <label className="modal-label flex flex-col gap-2 text-sm/6 font-medium text-taupe-950 dark:text-white">
          Work email
          <input
            type="email"
            id="use-case-email"
            placeholder="you@company.com"
            autoComplete="email"
            className="rounded-lg border border-taupe-300 bg-white px-3 py-2 text-sm font-normal text-taupe-950 placeholder:text-taupe-500 focus:border-taupe-950 focus:outline-none focus:ring-2 focus:ring-taupe-300 dark:border-taupe-700 dark:bg-taupe-900 dark:text-white"
          />
        </label>
        <div
          className="modal-error min-h-[1.25rem] text-sm/5 text-red-700"
          id="modal-error"
        />
      </div>
      <div
        className="modal-body modal-body-success hidden flex-col items-center gap-3 px-6 py-8 text-center [&:not([hidden])]:flex"
        id="modal-body-success"
        hidden
      >
        <div className="inline-flex size-12 items-center justify-center rounded-full bg-taupe-100 text-2xl text-taupe-950 dark:bg-taupe-900 dark:text-white">
          ✓
        </div>
        <h4 className="font-display text-xl/8 text-taupe-950 dark:text-white">
          On its way.
        </h4>
        <p className="text-sm/6 text-taupe-700 dark:text-taupe-400" id="modal-success-text">
          —
        </p>
      </div>
      <div className="modal-foot flex items-center justify-end gap-3 border-t border-taupe-200 px-6 py-4 dark:border-taupe-800" id="modal-foot">
        <Button className="btn-primary" id="generate-btn" size="lg">
          Send my use case
        </Button>
      </div>
    </Modal>
  );
}
